import { useState, useRef, useEffect } from 'react';
import API from './api';

// ─── Constants ────────────────────────────────────────────────────────────────
const FINDING_TYPE_META = {
  NC:               { code: 'NC',  label: 'NON-CONFORMITY', color: '#e8001d', bg: 'rgba(232,0,29,0.13)', border: '#e8001d' },
  'Non-Conformity': { code: 'NC',  label: 'NON-CONFORMITY', color: '#e8001d', bg: 'rgba(232,0,29,0.13)', border: '#e8001d' },
  DEF:              { code: 'DEF', label: 'DEFICIENCY',      color: '#f0a500', bg: 'rgba(240,165,0,0.13)', border: '#f0a500' },
  Deficiency:       { code: 'DEF', label: 'DEFICIENCY',      color: '#f0a500', bg: 'rgba(240,165,0,0.13)', border: '#f0a500' },
  OBS:              { code: 'OBS', label: 'OBSERVATION',     color: '#00b0f0', bg: 'rgba(0,176,240,0.13)', border: '#00b0f0' },
  Observation:      { code: 'OBS', label: 'OBSERVATION',     color: '#00b0f0', bg: 'rgba(0,176,240,0.13)', border: '#00b0f0' },
};

const AGENTS_META = {
  ism:  { name: 'ISM Auditor',  code: 'ISM Code',    color: '#e8001d', icon: '⚓' },
  isps: { name: 'ISPS Auditor', code: 'ISPS Code',   color: '#0070c0', icon: '🛡️' },
  mlc:  { name: 'MLC Auditor',  code: 'MLC 2006',    color: '#00b050', icon: '👷' },
  iso:  { name: 'ISO Auditor',  code: 'ISO 9001/14001', color: '#7030a0', icon: '📋' },
};

const SEVERITY_COLOR = {
  NC: '#e8001d',
  'Non-Conformity': '#e8001d',
  Deficiency: '#f0a500',
  Observation: '#00b0f0',
  'N/A': '#555',
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function VesselAuditManagement({ setPage, onLogout }) {
  const [rawFinding, setRawFinding] = useState('');
  const [context, setContext]       = useState('');
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState('');
  const [activeAgent, setActiveAgent] = useState('ism');
  const [copied, setCopied]         = useState('');
  const [progress, setProgress]     = useState(0);
  const [progressStage, setProgressStage] = useState('');
  const [checklist, setChecklist]   = useState(null);  // {loaded, question_count, filename, file_size_kb}
  const [clUploading, setClUploading] = useState(false);
  const checklistInputRef = useRef();
  const progressTimerRef = useRef(null);

  useEffect(() => {
    API.get('/inspection/checklist/status')
      .then(r => setChecklist(r.data))
      .catch(() => {});
  }, []);

  const handleChecklistUpload = async (file) => {
    if (!file) return;
    setClUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await API.post('/inspection/checklist/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setChecklist(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Upload failed.');
    } finally {
      setClUploading(false);
    }
  };

  const PROGRESS_STAGES = [
    { target: 8,  label: 'Connecting to analysis engine…',        ms: 600  },
    { target: 25, label: 'Searching document library…',           ms: 2500 },
    { target: 45, label: 'Running ISM & ISPS specialist agents…', ms: 7000 },
    { target: 68, label: 'Running MLC & ISO specialist agents…',  ms: 7000 },
    { target: 85, label: 'Consolidating agent findings…',         ms: 5000 },
    { target: 95, label: 'Building final audit report…',          ms: 5000 },
  ];

  const startProgress = () => {
    if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
    setProgress(0);
    setProgressStage('Connecting to analysis engine…');
    let stageIdx = 0;
    let stagePctStart = 0;
    let stageStartTime = Date.now();
    const tick = () => {
      if (stageIdx >= PROGRESS_STAGES.length) return;
      const { target, label, ms } = PROGRESS_STAGES[stageIdx];
      const elapsed = Date.now() - stageStartTime;
      const fraction = Math.min(elapsed / ms, 1);
      const pct = Math.round(stagePctStart + (target - stagePctStart) * fraction);
      setProgress(pct);
      setProgressStage(label);
      if (fraction >= 1) { stagePctStart = target; stageStartTime = Date.now(); stageIdx++; }
      progressTimerRef.current = setTimeout(tick, 50);
    };
    progressTimerRef.current = setTimeout(tick, 50);
  };

  const handleAnalyze = async () => {
    if (!rawFinding.trim()) { setError('Please describe the finding.'); return; }
    setError('');
    setLoading(true);
    setResult(null);
    startProgress();
    try {
      const res = await API.post('/inspection/analyze', {
        raw_finding: rawFinding,
        context: context,
      });
      setProgress(100);
      setProgressStage('Analysis complete!');
      setResult(res.data);
      setActiveAgent('ism');
    } catch (e) {
      if (e.response?.status === 401) onLogout();
      setError(e.response?.data?.detail || 'Analysis failed. Please check API connectivity.');
    } finally {
      if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setRawFinding('');
    setContext('');
    setError('');
    setProgress(0);
    setProgressStage('');
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const copyAll = () => {
    if (!result?.final) return;
    const f = result.final;
    const ftMeta = FINDING_TYPE_META[f.finding_type] || FINDING_TYPE_META['Observation'];
    const cas = (f.ca_items || []).map((c, i) =>
      `CA-${i + 1}: ${c.action}\n   Target: ${c.measurable_target}\n   Evidence: ${c.evidence_required}`
    ).join('\n');
    const pas = (f.pa_items || []).map((p, i) =>
      `PA-${i + 1}: ${p.action}\n   Target: ${p.measurable_target}\n   Evidence: ${p.evidence_required}`
    ).join('\n');
    const shore = (f.shore010_matches || []).map(m =>
      `${m.question_id}${m.question_text ? ' — ' + m.question_text : ''} [${m.match_type}]: ${m.explanation}`
    ).join('\n');
    const text = `FINDING TYPE: [${ftMeta.code}] ${ftMeta.label}
RATIONALE: ${f.finding_type_rationale}
REFERENCES: ${(f.references || []).join(', ')}

FINDING:
${f.finding}

CORRECTIVE ACTIONS (CA):
${cas}

PREVENTIVE ACTIONS (PA):
${pas}${shore ? `\n\nSSE SHORE-010 MATCHES:\n${shore}` : ''}`;
    copyText(text, 'all');
  };

  const ft = result?.final?.finding_type
    ? (FINDING_TYPE_META[result.final.finding_type] || FINDING_TYPE_META['Observation'])
    : null;

  return (
    <div style={s.wrapper}>
      {/* HEADER */}
      <header style={s.header}>
        <button style={s.backBtn} onClick={() => setPage('dashboard')}>← Back</button>
        <div style={s.headerLeft}>
          <div style={s.headerTitle}>Vessel Audit Management</div>
          <div style={s.headerSub}>4-Agent Expert Analysis System</div>
        </div>
        <div style={s.headerBadges}>
          {Object.entries(AGENTS_META).map(([key, a]) => (
            <span key={key} style={{ ...s.agentBadge, borderColor: a.color, color: a.color }}>
              {a.icon} {a.code}
            </span>
          ))}
        </div>
      </header>

      <main style={s.main}>
        {!result ? (
          /* ── INPUT PANEL ── */
          <div style={s.inputPanel}>
            <div style={s.inputCard}>
              <div style={s.sectionLabel}>01 — AUDIT FINDING</div>
              <textarea
                style={s.textarea}
                value={rawFinding}
                onChange={e => setRawFinding(e.target.value)}
                placeholder="Describe what was observed on board. Use factual language.&#10;&#10;Example: The Fire Detection System was found disabled for Zone 2 (All Detectors — Void Deck) at 21:44 hrs. AB reported hot work done. No supportive paperwork or permit in the system."
                rows={10}
              />
            </div>
            <div style={s.inputCard}>
              <div style={s.sectionLabel}>02 — ADDITIONAL CONTEXT (OPTIONAL)</div>
              <textarea
                style={{ ...s.textarea }}
                value={context}
                onChange={e => setContext(e.target.value)}
                placeholder="Vessel type, flag, trade area, date of observation, department..."
                rows={3}
              />
            </div>
            {/* Checklist status */}
            <div style={s.clCard}>
              <div style={s.sectionLabel}>03 — CHECKLIST SOURCE</div>
              <div style={s.clRow}>
                {checklist?.loaded ? (
                  <div style={s.clInfo}>
                    <span style={s.clDot} />
                    <span style={s.clName}>{checklist.filename}</span>
                    <span style={s.clMeta}>{checklist.question_count} questions · {checklist.file_size_kb} KB</span>
                  </div>
                ) : (
                  <div style={s.clInfo}>
                    <span style={{ ...s.clDot, background: '#555' }} />
                    <span style={{ ...s.clName, color: 'var(--grey)' }}>No checklist loaded</span>
                  </div>
                )}
                <button
                  style={{ ...s.clBtn, opacity: clUploading ? 0.6 : 1 }}
                  onClick={() => checklistInputRef.current?.click()}
                  disabled={clUploading}
                >
                  {clUploading ? 'Uploading…' : checklist?.loaded ? 'Update' : 'Upload XLSM'}
                </button>
                <input
                  ref={checklistInputRef}
                  type="file"
                  accept=".xlsm,.xlsx"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) handleChecklistUpload(f);
                    e.target.value = '';
                  }}
                />
              </div>
            </div>

            {error && <div style={s.error}>{error}</div>}
            <button
              style={{ ...s.analyzeBtn, opacity: loading ? 0.7 : 1 }}
              onClick={handleAnalyze}
              disabled={loading}
            >
              {loading
                ? <span>⏳ Running 4 Specialist Agents…</span>
                : <span>⚡ ANALYZE FINDING — 4 AGENTS</span>}
            </button>
            {loading && (
              <div style={s.progressContainer}>
                <div style={s.progressHeader}>
                  <span style={s.progressStageText}>{progressStage}</span>
                  <span style={s.progressPct}>{progress}%</span>
                </div>
                <div style={s.progressTrack}>
                  <div style={{ ...s.progressFill, width: `${progress}%` }} />
                </div>
                <div style={s.loadingGrid}>
                  {Object.entries(AGENTS_META).map(([key, a]) => (
                    <div key={key} style={{ ...s.loadingCard, borderColor: a.color }}>
                      <div style={s.loadingIcon}>{a.icon}</div>
                      <div style={{ ...s.loadingName, color: a.color }}>{a.name}</div>
                      <div style={s.loadingDots}>analyzing…</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── RESULT PANEL ── */
          <div style={s.resultLayout}>

            {/* LEFT — Agent Tabs + Final Finding */}
            <div style={s.leftCol}>

              {/* Finding Type Banner */}
              <div style={{
                ...s.ftBanner,
                background: ft?.bg,
                borderLeft: `6px solid ${ft?.color}`,
                boxShadow: `0 0 20px ${ft?.color}22`,
              }}>
                <div style={s.ftBannerTop}>
                  <span style={{ ...s.ftCode, color: ft?.color, borderColor: ft?.color, background: `${ft?.color}18` }}>
                    {ft?.code}
                  </span>
                  <span style={{ ...s.ftFullLabel, color: ft?.color }}>{ft?.label}</span>
                </div>
                <div style={s.ftRationale}>{result.final.finding_type_rationale}</div>
              </div>

              {/* Agent Selector Tabs */}
              <div style={s.tabBar}>
                {Object.entries(AGENTS_META).map(([key, a]) => {
                  const agent = result.agents?.[key] || {};
                  const isActive = activeAgent === key;
                  return (
                    <button
                      key={key}
                      style={{
                        ...s.tab,
                        borderColor: isActive ? a.color : 'var(--navy-border)',
                        color: isActive ? a.color : 'var(--grey)',
                        background: isActive ? `${a.color}18` : 'transparent',
                      }}
                      onClick={() => setActiveAgent(key)}
                    >
                      <span style={s.tabIcon}>{a.icon}</span>
                      <span style={s.tabName}>{a.name}</span>
                      {agent.applicable && (
                        <span style={{
                          ...s.tabBadge,
                          background: SEVERITY_COLOR[agent.severity] || '#555',
                        }}>
                          {agent.severity}
                        </span>
                      )}
                      {!agent.applicable && (
                        <span style={{ ...s.tabBadge, background: '#444' }}>N/A</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Active Agent Assessment */}
              {result.agents && (
                <AgentCard agent={result.agents[activeAgent]} meta={AGENTS_META[activeAgent]} />
              )}

              {/* Final Finding */}
              <div style={s.finalCard}>
                <div style={s.finalHeader}>
                  <div style={s.sectionLabel}>FINAL AUDIT FINDING</div>
                  <button
                    style={{ ...s.copyBtn, ...(copied === 'all' ? s.copySuccess : {}) }}
                    onClick={copyAll}
                  >
                    {copied === 'all' ? '✓ Copied' : '📋 Copy All'}
                  </button>
                </div>
                <div style={s.findingText}>{result.final.finding}</div>
                {(result.final.references || []).length > 0 && (
                  <div style={s.refChips}>
                    {result.final.references.map((r, i) => (
                      <span key={i} style={s.refChip}>{r}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Shore-010 Matches */}
              {(result.final.shore010_matches || []).length > 0 && (
                <Shore010Section matches={result.final.shore010_matches} />
              )}

              {/* Library References */}
              {(result.references_used || []).length > 0 && (
                <div style={s.libCard}>
                  <div style={s.sectionLabel}>LIBRARY REFERENCES USED</div>
                  {result.references_used.map((r, i) => (
                    <div key={i} style={s.libRef}>📄 {r}</div>
                  ))}
                </div>
              )}
            </div>

            {/* RIGHT — CA / PA with Evidence Upload */}
            <div style={s.rightCol}>
              <div style={s.capaHeader}>
                <div style={s.sectionLabel}>CORRECTIVE & PREVENTIVE ACTIONS</div>
              </div>

              {(result.final.ca_items || []).map((item) => (
                <ActionCard key={item.id} item={item} type="CA" color="#e8001d" />
              ))}

              {(result.final.pa_items || []).map((item) => (
                <ActionCard key={item.id} item={item} type="PA" color="#0070c0" />
              ))}

              <button style={s.resetBtn} onClick={handleReset}>
                ← Analyze New Finding
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ─── Sub-Components ───────────────────────────────────────────────────────────

function AgentCard({ agent, meta }) {
  if (!agent) return null;
  const applicable = agent.applicable;
  return (
    <div style={{
      ...s.agentCard,
      borderColor: applicable ? meta.color : 'var(--navy-border)',
      opacity: applicable ? 1 : 0.6,
    }}>
      <div style={s.agentCardHeader}>
        <span style={{ fontSize: '18px' }}>{meta.icon}</span>
        <span style={{ ...s.agentCardName, color: meta.color }}>{meta.name}</span>
        <span style={{
          ...s.severityBadge,
          background: SEVERITY_COLOR[agent.severity] || '#555',
        }}>
          {agent.severity}
        </span>
      </div>
      {!applicable && (
        <div style={s.naMessage}>Not applicable to this finding from {meta.code} perspective.</div>
      )}
      {applicable && (
        <>
          <div style={s.agentAssessment}>{agent.assessment}</div>
          {agent.non_conformity_basis && (
            <div style={s.ncBasis}>
              <span style={s.ncBasisLabel}>Requirement breached: </span>
              {agent.non_conformity_basis}
            </div>
          )}
          {(agent.references || []).length > 0 && (
            <div style={s.refChips}>
              {agent.references.map((r, i) => (
                <span key={i} style={s.refChip}>{r}</span>
              ))}
            </div>
          )}
          {agent.finding_contribution && (
            <div style={s.contribution}>
              <span style={s.contributionLabel}>↳ Finding contribution: </span>
              {agent.finding_contribution}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Shore010Section({ matches }) {
  const matchTypeColor = { Direct: '#e8001d', Partial: '#f0a500' };
  return (
    <div style={s.shore010Card}>
      <div style={s.sectionLabel}>SSE SHORE-010A — CHECKLIST QUESTIONS MATCHED</div>
      <div style={s.shore010Count}>{matches.length} question{matches.length !== 1 ? 's' : ''} from checklist addressed by this finding</div>
      {matches.map((m, i) => (
        <div key={i} style={s.shore010Row}>
          <div style={s.shore010Left}>
            <span style={s.shore010Id}>{m.question_id}</span>
            <span style={{
              ...s.matchTypeBadge,
              background: matchTypeColor[m.match_type] || '#555',
            }}>
              {m.match_type}
            </span>
          </div>
          <div style={s.shore010Right}>
            {m.question_text && (
              <div style={s.shore010QText}>{m.question_text}</div>
            )}
            <div style={s.shore010Expl}>{m.explanation}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ActionCard({ item, type, color }) {
  return (
    <div style={{ ...s.actionCard, borderLeft: `4px solid ${color}` }}>
      <div style={s.actionHeader}>
        <span style={{ ...s.actionType, color, borderColor: color }}>{item.id}</span>
        <span style={{ ...s.actionTypeLabel, color }}>{type === 'CA' ? 'CORRECTIVE ACTION' : 'PREVENTIVE ACTION'}</span>
      </div>

      <div style={s.actionText}>{item.action}</div>

      <div style={s.actionMeta}>
        <div style={s.metaRow}>
          <span style={s.metaLabel}>📏 Measurable Target</span>
          <span style={s.metaValue}>{item.measurable_target}</span>
        </div>
        <div style={s.metaRow}>
          <span style={s.metaLabel}>📎 Evidence Required</span>
          <span style={s.metaValue}>{item.evidence_required}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  wrapper: { minHeight: '100vh', background: 'var(--navy)', fontFamily: 'var(--font-sans)' },

  header: {
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 32px', height: '68px', display: 'flex', alignItems: 'center', gap: '20px',
  },
  backBtn: {
    background: 'transparent', border: 'none', color: 'var(--grey)',
    cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap',
  },
  headerLeft: { flex: 1 },
  headerTitle: { fontSize: '17px', fontWeight: '700', color: 'var(--white)' },
  headerSub: { fontSize: '11px', color: 'var(--grey)', marginTop: '2px' },
  headerBadges: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  agentBadge: {
    fontSize: '10px', fontFamily: 'var(--font-mono)', padding: '3px 8px',
    borderRadius: '4px', border: '1px solid', whiteSpace: 'nowrap',
  },

  main: { padding: '32px', maxWidth: '1400px', margin: '0 auto' },

  // Input panel
  inputPanel: { maxWidth: '720px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' },
  inputCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '24px',
  },
  sectionLabel: {
    fontFamily: 'var(--font-mono)', fontSize: '10px', fontWeight: '700',
    letterSpacing: '2px', color: 'var(--gold)', marginBottom: '12px',
  },
  textarea: {
    width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-light)',
    borderRadius: '8px', padding: '12px 14px', color: 'var(--white)',
    fontSize: '13px', outline: 'none', fontFamily: 'var(--font-mono)',
    resize: 'vertical', lineHeight: '1.7', boxSizing: 'border-box',
  },
  error: {
    background: 'rgba(232,0,29,0.1)', border: '1px solid rgba(232,0,29,0.3)',
    borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#ff6b7a',
  },
  analyzeBtn: {
    width: '100%', background: 'var(--red)', border: 'none', borderRadius: '10px',
    padding: '18px', color: 'white', fontSize: '14px', fontWeight: '700',
    letterSpacing: '1px', cursor: 'pointer',
  },
  progressContainer: { display: 'flex', flexDirection: 'column', gap: '12px' },
  progressHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  progressStageText: { fontSize: '12px', color: 'var(--grey-light)', fontFamily: 'var(--font-mono)' },
  progressPct: { fontSize: '20px', fontWeight: '800', color: 'var(--red)', fontFamily: 'var(--font-mono)' },
  progressTrack: {
    height: '6px', background: 'var(--navy-border)', borderRadius: '3px', overflow: 'hidden',
  },
  progressFill: {
    height: '100%', background: 'linear-gradient(90deg, #e8001d, #ff6b6b)',
    borderRadius: '3px', transition: 'width 0.1s linear',
  },
  loadingGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginTop: '4px',
  },
  loadingCard: {
    background: 'var(--navy-mid)', border: '1px solid',
    borderRadius: '10px', padding: '16px 12px', textAlign: 'center',
  },
  loadingIcon: { fontSize: '22px', marginBottom: '6px' },
  loadingName: { fontSize: '11px', fontWeight: '700', fontFamily: 'var(--font-mono)', marginBottom: '4px' },
  loadingDots: { fontSize: '11px', color: 'var(--grey)' },

  // Result layout
  resultLayout: { display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '28px', alignItems: 'start' },
  leftCol: { display: 'flex', flexDirection: 'column', gap: '16px' },
  rightCol: { display: 'flex', flexDirection: 'column', gap: '16px' },

  // Finding type banner
  ftBanner: {
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '18px 22px',
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  ftBannerTop: { display: 'flex', alignItems: 'center', gap: '14px' },
  ftCode: {
    fontSize: '22px', fontWeight: '900', letterSpacing: '3px',
    fontFamily: 'var(--font-mono)', border: '2px solid',
    borderRadius: '6px', padding: '4px 14px', lineHeight: '1',
  },
  ftFullLabel: {
    fontSize: '16px', fontWeight: '800', letterSpacing: '3px',
    fontFamily: 'var(--font-mono)',
  },
  ftRationale: { fontSize: '12px', color: 'var(--grey-light)', lineHeight: '1.6', paddingLeft: '2px' },

  // Agent tabs
  tabBar: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' },
  tab: {
    background: 'transparent', border: '1px solid', borderRadius: '8px',
    padding: '10px 8px', cursor: 'pointer', display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '4px', transition: 'all 0.15s',
  },
  tabIcon: { fontSize: '16px' },
  tabName: { fontSize: '10px', fontWeight: '700', fontFamily: 'var(--font-mono)' },
  tabBadge: {
    fontSize: '9px', padding: '2px 6px', borderRadius: '4px',
    color: 'white', fontWeight: '700', fontFamily: 'var(--font-mono)',
  },

  // Agent card
  agentCard: {
    background: 'var(--navy-mid)', border: '1px solid',
    borderRadius: '12px', padding: '20px',
  },
  agentCardHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' },
  agentCardName: { fontSize: '13px', fontWeight: '700', flex: 1 },
  severityBadge: {
    fontSize: '10px', padding: '3px 8px', borderRadius: '4px',
    color: 'white', fontWeight: '700', fontFamily: 'var(--font-mono)',
  },
  naMessage: { fontSize: '13px', color: 'var(--grey)', fontStyle: 'italic' },
  agentAssessment: { fontSize: '13px', lineHeight: '1.7', color: 'var(--white)', marginBottom: '12px' },
  ncBasis: {
    fontSize: '12px', color: 'var(--grey-light)', background: 'var(--navy)',
    borderRadius: '6px', padding: '10px 12px', marginBottom: '10px',
  },
  ncBasisLabel: { fontWeight: '700', color: 'var(--gold)' },
  contribution: {
    fontSize: '12px', color: 'var(--grey-light)', borderTop: '1px solid var(--navy-border)',
    paddingTop: '10px', marginTop: '4px', fontStyle: 'italic',
  },
  contributionLabel: { fontWeight: '600', fontStyle: 'normal', color: 'var(--grey)' },

  // Final finding card
  finalCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '20px',
  },
  finalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' },
  findingText: { fontSize: '13px', lineHeight: '1.8', color: 'var(--white)', marginBottom: '14px' },
  refChips: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
  refChip: {
    fontSize: '10px', fontFamily: 'var(--font-mono)', padding: '3px 8px',
    background: 'rgba(255,255,255,0.06)', border: '1px solid var(--navy-border)',
    borderRadius: '4px', color: 'var(--grey-light)',
  },

  // Shore-010 section
  shore010Card: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '20px',
  },
  shore010Count: {
    fontSize: '11px', color: 'var(--grey)', fontFamily: 'var(--font-mono)',
    marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid var(--navy-border)',
  },
  shore010Row: {
    display: 'flex', gap: '14px', alignItems: 'flex-start',
    padding: '12px 0', borderBottom: '1px solid var(--navy-border)',
  },
  shore010Left: { display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start', minWidth: '80px' },
  shore010Right: { flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' },
  shore010Id: { fontSize: '12px', fontWeight: '700', fontFamily: 'var(--font-mono)', color: 'var(--gold)' },
  matchTypeBadge: { fontSize: '9px', padding: '2px 6px', borderRadius: '4px', color: 'white', fontWeight: '700' },
  shore010QText: {
    fontSize: '12px', fontWeight: '600', color: 'var(--white)',
    lineHeight: '1.5', fontStyle: 'italic',
  },
  shore010Expl: { fontSize: '12px', color: 'var(--grey-light)', lineHeight: '1.5' },

  // Library refs
  libCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '16px 20px',
  },
  libRef: { fontSize: '12px', color: 'var(--grey)', padding: '4px 0', fontFamily: 'var(--font-mono)' },

  // Action cards
  capaHeader: { marginBottom: '4px' },
  actionCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '20px',
  },
  actionHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' },
  actionType: {
    fontSize: '12px', fontWeight: '800', fontFamily: 'var(--font-mono)',
    border: '1px solid', borderRadius: '4px', padding: '3px 8px',
  },
  actionTypeLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', fontFamily: 'var(--font-mono)' },
  actionText: { fontSize: '13px', lineHeight: '1.7', color: 'var(--white)', marginBottom: '14px' },
  actionMeta: {
    background: 'var(--navy)', borderRadius: '8px', padding: '12px 14px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  metaRow: { display: 'flex', flexDirection: 'column', gap: '3px' },
  metaLabel: { fontSize: '10px', fontWeight: '700', color: 'var(--gold)', fontFamily: 'var(--font-mono)' },
  metaValue: { fontSize: '12px', color: 'var(--grey-light)', lineHeight: '1.5' },

  // Checklist card
  clCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '16px 24px',
  },
  clRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  clInfo: { flex: 1, display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 },
  clDot: {
    width: '8px', height: '8px', borderRadius: '50%',
    background: '#00b050', flexShrink: 0,
  },
  clName: {
    fontSize: '12px', fontFamily: 'var(--font-mono)',
    color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  clMeta: { fontSize: '11px', color: 'var(--grey)', whiteSpace: 'nowrap' },
  clBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    borderRadius: '6px', padding: '6px 14px', color: 'var(--grey-light)',
    fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
  },

  // Shared
  copyBtn: {
    background: 'var(--navy)', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 14px', borderRadius: '6px',
    fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  copySuccess: { borderColor: '#00b050', color: '#00b050' },
  resetBtn: {
    width: '100%', background: 'transparent', border: '1px solid var(--navy-light)',
    borderRadius: '8px', padding: '12px', color: 'var(--grey)',
    fontSize: '12px', cursor: 'pointer', marginTop: '8px',
  },
};
